import { useId } from 'react';
import { LIMITE_TEXTO, LIMITE_TEXTO_LONGO } from '@/lib/mascaras';
import type { CampoExtra } from '@/lib/catalogo';

// Campos extras de um item de catálogo, renderizados a partir da definição.
//
// O catálogo guarda a **pergunta** (`infraestrutura_itens.campos_extras`); a
// instância guarda a **resposta** (`infraestrutura.valores_extras`). Este
// componente é a ponte entre as duas: recebe o esquema e o objeto de valores, e
// devolve o objeto atualizado.
//
// Enquanto o form builder completo não existe (Fase 2, secao 10), esta é a
// parte dele que o padrão fixo + JSON precisa: o admin declara os campos, o
// cliente responde, e nenhum dos dois escreve código.

interface Props {
  campos: CampoExtra[];
  valores: Record<string, unknown>;
  onChange: (valores: Record<string, unknown>) => void;
  desabilitado?: boolean;
}

const CLASSE =
  'w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 disabled:opacity-60';

export default function CamposExtras({ campos, valores, onChange, desabilitado = false }: Props) {
  const idBase = useId();

  if (campos.length === 0) return null;

  const alterar = (chave: string, valor: unknown) => onChange({ ...valores, [chave]: valor });

  return (
    <fieldset className="border border-background-300/60 rounded-md p-4">
      <legend className="px-1.5 text-sm font-medium text-foreground-700">
        Características do item
      </legend>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {campos.map((campo) => {
          const id = `${idBase}-${campo.chave}`;
          const valor = valores[campo.chave];

          if (campo.tipo === 'booleano') {
            return (
              <label key={campo.chave} className="flex items-center gap-2 text-sm sm:pt-7">
                <input
                  id={id}
                  type="checkbox"
                  checked={valor === true}
                  disabled={desabilitado}
                  onChange={(e) => alterar(campo.chave, e.target.checked)}
                  className="w-4 h-4 accent-primary-500"
                />
                <span className="text-foreground-700">{campo.rotulo}</span>
              </label>
            );
          }

          return (
            <div key={campo.chave}>
              <label htmlFor={id} className="block text-sm font-medium text-foreground-700 mb-1.5">
                {campo.rotulo}
                {campo.obrigatorio && <span className="text-red-500 ml-0.5">*</span>}
              </label>

              {campo.tipo === 'textarea' ? (
                <textarea
                  id={id}
                  rows={2}
                  maxLength={LIMITE_TEXTO_LONGO}
                  value={typeof valor === 'string' ? valor : ''}
                  disabled={desabilitado}
                  onChange={(e) => alterar(campo.chave, e.target.value)}
                  className={CLASSE}
                />
              ) : campo.tipo === 'select' ? (
                <select
                  id={id}
                  value={typeof valor === 'string' ? valor : ''}
                  disabled={desabilitado}
                  onChange={(e) => alterar(campo.chave, e.target.value || null)}
                  className={CLASSE}
                >
                  <option value="">Selecione...</option>
                  {(campo.opcoes ?? []).map((o) => (
                    <option key={o.valor} value={o.valor}>
                      {o.rotulo}
                    </option>
                  ))}
                </select>
              ) : campo.tipo === 'multiselect' ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {(campo.opcoes ?? []).map((o) => {
                    const lista = Array.isArray(valor) ? (valor as string[]) : [];
                    const marcado = lista.includes(o.valor);
                    return (
                      <button
                        key={o.valor}
                        type="button"
                        disabled={desabilitado}
                        aria-pressed={marcado}
                        onClick={() =>
                          alterar(
                            campo.chave,
                            marcado ? lista.filter((v) => v !== o.valor) : [...lista, o.valor],
                          )
                        }
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                          marcado
                            ? 'bg-primary-500 text-background-50 border-primary-500'
                            : 'bg-background-50 text-foreground-700 border-background-300'
                        }`}
                      >
                        {o.rotulo}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <input
                  id={id}
                  type={campo.tipo === 'data' ? 'date' : 'text'}
                  inputMode={campo.tipo === 'numero' ? 'decimal' : undefined}
                  maxLength={LIMITE_TEXTO}
                  value={valor === null || valor === undefined ? '' : String(valor)}
                  disabled={desabilitado}
                  onChange={(e) => {
                    const bruto = e.target.value;
                    if (campo.tipo === 'numero') {
                      const limpo = bruto.replace(/[^0-9.,]/g, '').replace(',', '.');
                      // Guarda número, não string: um relatório que soma
                      // capacidade não pode depender de o valor ter sido
                      // digitado com vírgula ou com ponto.
                      alterar(campo.chave, limpo === '' ? null : Number(limpo));
                      return;
                    }
                    alterar(campo.chave, bruto || null);
                  }}
                  className={CLASSE}
                />
              )}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
